/* Copyright (c) 2020-present CloudIO Inc */

import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { buttonSize, buttonVariant } from '../../registry/constants';
import { defaultValue } from './ActionButton';
import { isValidButtonColor } from '../Button/Button';
import { analyzeActionButton } from './ActionButtonAnalyze';

const ActionButtonManager: ComponentManagerFn<'ActionButton'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'ActionButton'>(platform, 'ActionButton'),
    type: 'ActionButton',
    name: 'Action Button',
    defaultValue,
    props: {
      action: {
        label: 'Action',
        type: 'select',
        required: true,
        options: toOptions([
          'Clear And Create',
          'Clear',
          'Create',
          'Delete Selected',
          'Delete',
          'Query',
          'Refresh',
          'Reset',
          'Save Transaction',
          'Save',
        ]),
      },
      alias: {
        label: 'Datastore Alias',
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
          'TransientDataStore',
        ],
      },
      transactionCode: {
        label: 'Transaction Code',
        type: 'string',
      },
      silent: {
        label: 'Silent',
        type: 'boolean',
      },
      consolidatedFeedback: {
        label: 'Consolidated Feedback',
        type: 'string',
      },
      force: {
        label: 'Force',
        type: 'boolean',
      },
      addOnTop: {
        label: 'Add On Top',
        type: 'boolean',
      },
      markAsDirty: {
        label: 'Mark As Dirty',
        type: 'boolean',
      },
      confirmationDialogTitle: {
        label: 'Confirmation Dialog Title',
        type: 'expression',
        returnType: async () => 'string',
      },
      confirmationMessage: {
        label: 'Confirmation Message',
        type: 'expression',
        returnType: async () => 'string',
      },
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
      },
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
        required: true,
        filterColor: isValidButtonColor,
      },
      fullWidth: {
        label: 'Full Width',
        type: 'boolean',
        helpText:
          'If checked, the button will take up the full width of its container.',
      },
      size: {
        label: 'Size',
        type: 'select',
        options: buttonSize,
        required: true,
      },
      startIcon: {
        label: 'Start Icon',
        type: 'iconselect',
        helpText: 'Icon to be placed before the text.',
        parentColorProp: 'color',
      },
      text: {
        label: 'Text',
        type: 'expression',
        returnType: async () => 'string',
      },
      hotKey: {
        label: 'Hot Key',
        type: 'string',
        maxLength: 1,
        helpText: (item: RequiredItem<'ActionButton'>) => {
          const key =
            item.props.hotKey ||
            (item.props.text.type === 'text' && item.props.text.value?.length
              ? item.props.text.value.charAt(0)
              : '<Hot Key>');

          return `A shortcut key to be mapped for this button click event. User can use Ctrl+Shift+${key} or Cmd+Shift+${key} as a keyboard shortcut instead of a mouse click on this button.`;
        },
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: buttonVariant,
        required: true,
      },
    },
    getDisplayText: (item) => item.props.alias ?? '',
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    analyze: analyzeActionButton,
    isPropertyVisible(prop, itemProps): boolean {
      switch (prop) {
        case 'transactionCode':
        case 'consolidatedFeedback':
          return itemProps.action === 'Save Transaction';
        case 'silent':
          return (
            itemProps.action === 'Save Transaction' ||
            itemProps.action === 'Save'
          );
        case 'alias':
          return itemProps.action !== 'Save Transaction';
        case 'force':
          return itemProps.action === 'Refresh';
        case 'addOnTop':
        case 'markAsDirty':
          return (
            itemProps.action === 'Create' ||
            itemProps.action === 'Clear And Create'
          );
        case 'confirmationDialogTitle':
        case 'confirmationMessage':
          return (
            itemProps.action === 'Delete' ||
            itemProps.action === 'Delete Selected'
          );
        default:
          return true;
      }
    },
    category: 'Forms',
    replaceableWith: ['IconButton', 'Button'],
    icon: { iconName: 'rocket' },
    description: `**Overview**<br/>
The Action Button is a specialized "Smart Button" pre-wired with standard Data Store logic.
Unlike a generic Button (which requires you to write a Controller Function or attach a Trigger to do anything), the Action Button comes with built-in handlers for common CRUD operations like Save, Create, Delete, Query, and Refresh. You simply select the action and the target Data Store, and the platform handles the execution logic automatically.

**1. Core Logic (The Action)**<br/>
The behavior of the button is defined by the **Action** property. Selecting an action often reveals specific settings relevant to that operation.
* **Save:** Commits changes in the target Data Store to the database. (Option: **Silent** suppresses the default "Saved Successfully" message).
* **Create:** Adds a new, empty record to the Data Store. (Options: **Add On Top** prepends the row; **Mark As Dirty** marks it as modified immediately).
* **Delete:** Deletes the currently active record. (Option: **Confirmation Dialog** to prompt user before deleting).
* **Delete Selected:** Deletes all checked rows (requires Row Selection enabled).
* **Query:** Triggers a fetch request to reload data from the server based on current filters.
* **Clear:** Empties the local Data Store (removes all records from view).
* **Reset:** Reverts changes made to the current record (Undo).
* **Refresh:** Re-fetches the specific active record from the server. (Option: **Force** bypasses cache).
* **Save Transaction:** Commits a complex, multi-store transaction.

**2. Target Configuration**<br/>
* **Datastore Alias:** Select the Data Store (e.g., "Employees", "Orders") that this button should operate on.
* **Note:** This is hidden for "Save Transaction" as transactions typically span multiple stores or are defined globally.

**3. Visual Appearance**<br/>
The Action Button shares the same styling engine as the standard Button component.
* **Variant:** Contained (Solid), Outlined (Bordered), or Text (Link-style).
* **Color:** Theme color (e.g., Primary, Secondary, Error).
* **Start Icon:** Adds a visual symbol (e.g., a "Floppy Disk" for Save, "Trash Can" for Delete).
* **Full Width:** Stretches the button to fill its container.

**4. User Interaction & Safety**<br/>
* **Disable Condition:** A boolean expression (e.g., \`!$.user.isAdmin\`). If true, the button is greyed out and unclickable.
* **Confirmation:** For destructive actions (Delete), you can define a **Confirmation Dialog Title** and **Message** (e.g., "Are you sure? This cannot be undone.") to prevent accidental data loss.
* **Hot Key:** Assign a keyboard shortcut (e.g., "S" for Save). The user can trigger the action by pressing Ctrl+Shift+S (or Cmd+Shift+S).`,
  };
};

export default ActionButtonManager;
